import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react'
import { useRouter, usePathname, useSearchParams } from '@/routing/navigation'
import { httpClient } from '@/lib/api/http-client'
import { authApi } from '@/lib/api/auth.api'
import { UserProfile } from '@/lib/api/client'
import { AnalyticsEvents, syncAnalyticsUser, track } from '@/lib/services/analytics'
import { resetWorkflowRunSubscriptions } from '@/lib/services/workflow-socket'
import { clearShareGuestIntent, ensureShareGuestIntent } from '@/lib/utils/share-guest-intent'
import { resetSidebarRunningThreads } from '@/components/layout/useSidebarThreads'
import { clearChatQueues } from '@/lib/services/chat-queue-storage'
import { SLACK_TWINS_CONNECT_PATH } from '@digital-twins/shared-types'

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: UserProfile | null
  loginWithResponse: (data: { user: UserProfile; isNewUser?: boolean } | UserProfile) => void
  refreshProfile: () => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  user: null,
  loginWithResponse: () => {},
  refreshProfile: async () => {},
  logout: async () => {},
})

export const useAuth = () => useContext(AuthContext)

const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/public',
  '/internal',
  '/share',
  '/auth',
  '/audiences/invites',
  // Slack Twins connect: the page itself sends a logged-out visitor to login
  // with a returnTo that keeps the one-time token.
  SLACK_TWINS_CONNECT_PATH,
]
const AUTH_PAGES = ['/login', '/register', '/forgot-password', '/reset-password']
const VERIFY_PATH = '/register/verify'

let profileFetchInProgress: Promise<boolean> | null = null

export function AuthProvider({ children }: { children: ReactNode }) {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [user, setUser] = useState<UserProfile | null>(null)
  // True when the initial auth probe was skipped (share route). Consumed once
  // by the redirect effect to trigger a lazy re-auth before redirecting to /login.
  const skippedAuthProbe = useRef(false)

  const clearAuthState = useCallback(() => {
    clearChatQueues()
    localStorage.removeItem('user')
    syncAnalyticsUser(null)
    setUser(null)
    setIsAuthenticated(false)
  }, [])

  const fetchProfile = useCallback(async (): Promise<boolean> => {
    if (profileFetchInProgress) return profileFetchInProgress

    const doFetch = async (): Promise<boolean> => {
      try {
        const response = await httpClient.get('/users/me')
        const profileData: UserProfile = response.data

        let mergedProfileData = profileData
        if (!Object.prototype.hasOwnProperty.call(profileData, 'avatarSeed')) {
          try {
            const rawCachedUser = localStorage.getItem('user')
            if (rawCachedUser) {
              const cachedUser = JSON.parse(rawCachedUser)
              if (cachedUser?.avatarSeed !== undefined) {
                mergedProfileData = { ...profileData, avatarSeed: cachedUser.avatarSeed }
              }
            }
          } catch { /* ignore */ }
        }

        setUser(mergedProfileData)
        setIsAuthenticated(true)
        localStorage.setItem('user', JSON.stringify(mergedProfileData))
        clearShareGuestIntent()
        syncAnalyticsUser(mergedProfileData)
        return true
      } catch (err: any) {
        if (err?.response?.status === 401 || err?.response?.status === 403) {
          clearAuthState()
        }
      }
      return false
    }

    profileFetchInProgress = doFetch()
    try {
      return await profileFetchInProgress
    } finally {
      profileFetchInProgress = null
    }
  }, [clearAuthState])

  const loginWithResponse = useCallback((data: { user: UserProfile; isNewUser?: boolean } | UserProfile) => {
    const userData = ('user' in data && data.user) ? data.user : data as UserProfile
    setUser(userData)
    setIsAuthenticated(true)
    localStorage.setItem('user', JSON.stringify(userData))
    clearShareGuestIntent()
    syncAnalyticsUser(userData)
    // user_logged_in is emitted server-side (auth.service login / OAuth) to avoid double-counting.

    if ('isNewUser' in data && data.isNewUser) {
      localStorage.setItem('showOnboardingTour', String(userData.id))
    }

    // Fetch full profile so fields like emailVerified are populated
    fetchProfile()

    try {
      const bc = new BroadcastChannel('fairgen-auth')
      bc.postMessage({ type: 'login' })
      bc.close()
    } catch { /* BroadcastChannel not supported */ }
  }, [fetchProfile])

  const refreshProfile = useCallback(async () => {
    await fetchProfile()
  }, [fetchProfile])

  const logout = useCallback(async () => {
    track(AnalyticsEvents.USER_LOGGED_OUT)
    await authApi._serverLogout()
    clearShareGuestIntent()
    clearAuthState()
    /* Both stores are module-level and survive an unmount on purpose, which is
       exactly why logging out has to empty them by hand: the sidebar dot would
       otherwise stay lit for the previous user's run, and the next session's
       fresh socket would re-join their run rooms on connect. */
    resetSidebarRunningThreads()
    resetWorkflowRunSubscriptions()

    try {
      const bc = new BroadcastChannel('fairgen-auth')
      bc.postMessage({ type: 'logout' })
      bc.close()
    } catch { /* BroadcastChannel not supported */ }

    router.push('/login')
  }, [clearAuthState, router])

  // Check auth on mount (cookie-based: call /users/me)
  useEffect(() => {
    const checkAuth = async () => {
      const newUserParam = searchParams.get('newUser')
      if (newUserParam === '1') {
        localStorage.setItem('showOnboardingTour', 'true')
        // Strip the one-shot param so a manual refresh of the post-payment
        // redirect URL (e.g. /?newUser=1 from a paid-first checkout) does not
        // re-trigger the onboarding tour after it has already been completed.
        try {
          const url = new URL(window.location.href)
          url.searchParams.delete('newUser')
          window.history.replaceState(
            window.history.state,
            '',
            `${url.pathname}${url.search}${url.hash}`,
          )
        } catch {
          /* no-op: history API unavailable */
        }
      }

      if (ensureShareGuestIntent(pathname)) {
        clearAuthState()
        skippedAuthProbe.current = true
        setIsLoading(false)
        return
      }

      await fetchProfile()
      setIsLoading(false)
    }
    checkAuth()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-tab auth sync via BroadcastChannel
  useEffect(() => {
    let bc: BroadcastChannel | null = null
    try {
      bc = new BroadcastChannel('fairgen-auth')
      bc.onmessage = (event) => {
        if (event.data?.type === 'logout') {
          clearAuthState()
          if (!PUBLIC_PATHS.some(p => pathname?.startsWith(p))) {
            router.push('/login')
          }
        } else if (event.data?.type === 'login') {
          fetchProfile()
        }
      }
    } catch { /* BroadcastChannel not supported */ }

    return () => { bc?.close() }
  }, [clearAuthState, fetchProfile, pathname, router])

  // Handle redirects based on auth state
  useEffect(() => {
    if (isLoading || !pathname) return

    const isPublicPath = PUBLIC_PATHS.some(path => pathname.startsWith(path))
    const isAuthPage = AUTH_PAGES.some(p => pathname.startsWith(p))

    // Redirect unverified users to the verify page
    if (isAuthenticated && user?.emailVerified === false && !pathname.startsWith(VERIFY_PATH)) {
      const email = user?.email
      router.push(email ? `${VERIFY_PATH}?email=${encodeURIComponent(email)}` : VERIFY_PATH)
      return
    }

    if (!isAuthenticated && !isPublicPath) {
      // If we skipped the initial auth probe (share route), the user may still
      // have a valid session. Run a lazy re-auth before sending them to /login.
      if (skippedAuthProbe.current) {
        skippedAuthProbe.current = false
        setIsLoading(true)
        fetchProfile().then(() => setIsLoading(false))
        return
      }
      router.push('/login')
    } else if (isAuthenticated && isAuthPage && !pathname.startsWith(VERIFY_PATH)) {
      router.push('/')
    }
  }, [isLoading, isAuthenticated, pathname, router, user, fetchProfile])

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, user, loginWithResponse, refreshProfile, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
